/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { Vector3 } from 'three';
import { useStore } from '../stores/common';
import { usePrimitiveStore } from '../stores/commonPrimitive';
import { useRefStore } from '../stores/commonRef';
import { Atom } from '../models/Atom';
import { Molecule } from '../models/Molecule';
import { isParticleCollection, withParticles } from '../models/beadModel';
import { Undoable } from './Undoable';
import {
  blockedWhileSimulating,
  copyRestraintAnchorToStore,
  markEngineDirty,
  refuseWhileMinimizing,
  transformRestraintAnchor,
} from '../view/engineSync';
import { cloneLabMoleculeLive } from './liveMoleculeClone';
import { cutMoleculeWithUndo } from './cutMolecule';

// Cut / Copy / Delete of single particles of a particle collection: N unbonded
// beads or Gay-Berne rods stored as ONE lab molecule (ROD400). The molecule is
// the collection's storage, not what the user sees or picks, so these act on the
// picked particles and leave the others alone.

// Store molecules that replace a built one with some particles more or less. The
// viewer carries the cell and the plate through a rebuild only when a molecule
// survives it, and a collection may be the lab's only molecule: without this a
// cut particle would send the barostat's cell back to its starting size.
const successors = new WeakSet<object>();
export const isParticleEditSuccessor = (m: object): boolean => successors.has(m);

interface PickedParticles {
  moleculeIndex: number;
  local: number[]; // ascending indices within the molecule
}

/**
 * The picked atoms that are particles of a collection, or null. A selection is
 * resolved to one collection: the one holding the anchor pick, else the first.
 */
export const pickedParticles = (): PickedParticles | null => {
  const { pickedAtomIndices, pickedAtomIndex } = usePrimitiveStore.getState();
  if (pickedAtomIndices.size === 0) return null;
  const molecules = useStore.getState().projectState.testMolecules ?? [];
  const anchor = pickedAtomIndices.has(pickedAtomIndex) ? pickedAtomIndex : Math.min(...pickedAtomIndices);
  let offset = 0;
  for (let k = 0; k < molecules.length; k++) {
    const n = molecules[k].atoms.length;
    if (anchor < offset + n) {
      if (!isParticleCollection(molecules[k])) return null;
      const local: number[] = [];
      for (const g of pickedAtomIndices) if (g >= offset && g < offset + n) local.push(g - offset);
      return { moleculeIndex: k, local: local.sort((a, b) => a - b) };
    }
    offset += n;
  }
  return null;
};

const liveClone = (index: number): Molecule | null => {
  const m = useStore.getState().projectState.testMolecules[index];
  return m ? cloneLabMoleculeLive(index) ?? Molecule.clone(m, true) : null;
};

const clipboardOf = (source: Molecule, local: readonly number[]): Molecule =>
  Molecule.clone(
    withParticles(
      source,
      local.map((i) => Atom.clone(source.atoms[i], true)),
      local.map((i) => source.beads!.typeOfAtom[i]),
    ),
  );

/** Copy the picked particles (new-molecule clones of what the user sees). */
export const copyPickedParticles = (): boolean => {
  const picked = pickedParticles();
  const source = picked && liveClone(picked.moleculeIndex);
  if (!picked || !source) return false;
  usePrimitiveStore.getState().set((state) => {
    state.copiedMolecule = clipboardOf(source, picked.local);
    state.cutMolecule = null;
  });
  return true;
};

const removePicked = (cut: boolean): boolean => {
  const picked = pickedParticles();
  if (!picked) return false;
  const { moleculeIndex, local } = picked;
  const store = useStore.getState().projectState.testMolecules;
  // Every particle picked: that is the molecule itself.
  if (local.length >= store[moleculeIndex].atoms.length) return cut ? cutMoleculeWithUndo(moleculeIndex) : false;
  if (refuseWhileMinimizing()) return false;
  const name = (cut ? 'Cut' : 'Delete') + (local.length > 1 ? ' Selected Particles' : ' Selected Particle');
  const removedSet = new Set(local);
  let removed: { index: number; atom: Atom; type: number }[] = [];
  let current: Molecule = store[moleculeIndex];

  // By identity; an edit since (a drag) gives the molecule a new object in the same slot.
  const slot = () => {
    const i = useStore.getState().projectState.testMolecules.indexOf(current);
    return i >= 0 ? i : moleculeIndex;
  };

  const replace = (at: number, next: Molecule) => {
    successors.add(next);
    useStore.getState().set((state) => {
      state.projectState.testMolecules[at] = next;
    });
    current = next;
    usePrimitiveStore.getState().setChanged(true);
  };

  const doRemove = () => {
    // Every time from the live frame: the rest of the lab has moved on since the
    // last Undo, and a Redo must not rewind it.
    const at = slot();
    const source = liveClone(at);
    if (!source?.beads) return;
    const types = source.beads.typeOfAtom;
    removed = local.map((i) => ({ index: i, atom: source.atoms[i], type: types[i] }));
    usePrimitiveStore.getState().set((state) => {
      if (cut) {
        state.cutMolecule = clipboardOf(source, local);
        state.copiedMolecule = null;
      }
      state.pickedMoleculeIndex = -1;
      state.pickedMoleculeIndices = new Set<number>();
      state.pickedAtomIndex = -1;
      state.pickedAtomIndices = new Set<number>();
    });
    const keep = source.atoms.map((_, i) => i).filter((i) => !removedSet.has(i));
    replace(
      at,
      withParticles(
        source,
        keep.map((i) => source.atoms[i]),
        keep.map((i) => types[i]),
      ),
    );
  };

  const doRestore = () => {
    // Back into the LIVE collection, not a snapshot of the whole box from before
    // the cut: the other particles stay where the run has taken them.
    const at = slot();
    const source = liveClone(at);
    if (!source?.beads) return;
    const atoms = [...source.atoms];
    const types = [...source.beads.typeOfAtom];
    for (const r of removed) {
      // Ascending, so each lands at its original index.
      atoms.splice(r.index, 0, Atom.clone(r.atom, true));
      types.splice(r.index, 0, r.type);
    }
    replace(at, withParticles(source, atoms, types));
    const offset = useStore
      .getState()
      .projectState.testMolecules.slice(0, at)
      .reduce((n, m) => n + m.atoms.length, 0);
    usePrimitiveStore.getState().set((state) => {
      state.pickedMoleculeIndex = -1;
      state.pickedMoleculeIndices = new Set<number>();
      state.pickedAtomIndex = offset + local[0];
      state.pickedAtomIndices = new Set<number>(local.map((i) => offset + i));
    });
  };

  doRemove();
  const undoable: Undoable = { name, timestamp: Date.now(), undo: doRestore, redo: doRemove };
  useStore.getState().addUndoable(undoable);
  const { loggable, logAction } = useStore.getState();
  if (loggable) logAction(name);
  return true;
};

/** The live atoms of the picked particles. */
export const pickedParticleAtoms = (): Atom[] => {
  const picked = pickedParticles();
  const live = picked && useRefStore.getState().moleculesRef?.current?.[picked.moleculeIndex];
  return picked && live ? picked.local.map((i) => live.atoms[i]).filter(Boolean) : [];
};

// Live and store atoms stay in step, and the worker hears of it: a placed particle
// is the new Reset target (as with a molecule's Translate).
const moveParticles = ({ moleculeIndex, local }: PickedParticles, d: Vector3) => {
  const live = useRefStore.getState().moleculesRef?.current?.[moleculeIndex];
  if (!live) return;
  for (const i of local) {
    const a = live.atoms[i];
    a.position.add(d);
    a.initialPosition?.copy(a.position);
    transformRestraintAnchor(a, (anchor) => anchor.add(d));
  }
  useStore.getState().set((state) => {
    const mol = state.projectState.testMolecules[moleculeIndex];
    for (const i of local) {
      const a = mol?.atoms[i];
      if (!a) continue;
      a.position.x += d.x;
      a.position.y += d.y;
      a.position.z += d.z;
      copyRestraintAnchorToStore(a, live.atoms[i]);
    }
  });
  usePrimitiveStore.getState().updateViewer();
  usePrimitiveStore.getState().setChanged(true);
  markEngineDirty({ moved: true });
};

const sameParticles = (a: PickedParticles, b: PickedParticles) =>
  a.moleculeIndex === b.moleculeIndex && a.local.length === b.local.length && a.local.every((v, k) => v === b.local[k]);

// ONE undoable per burst of moves (the X / Y / Z fields apply on every keystroke):
// later moves fold into the entry while it is still the newest on the stack.
let openMove: { picked: PickedParticles; total: Vector3; undoable: Undoable } | null = null;

/** Move the picked particles by `d`; refused during a run, as a molecule's Translate is. */
export const moveParticlesWithUndo = (d: Vector3): void => {
  const picked = pickedParticles();
  if (!picked || blockedWhileSimulating()) return;
  moveParticles(picked, d);
  const { undoManager, addUndoable } = useStore.getState();
  if (openMove && sameParticles(openMove.picked, picked) && undoManager.isLastUndo(openMove.undoable)) {
    openMove.total.add(d);
    return;
  }
  const total = d.clone();
  const name = picked.local.length > 1 ? 'Translate Selected Particles' : 'Translate Selected Particle';
  const undoable = {
    name,
    timestamp: Date.now(),
    blocked: blockedWhileSimulating,
    undo: () => moveParticles(picked, total.clone().negate()),
    redo: () => moveParticles(picked, total),
  } as Undoable;
  openMove = { picked, total, undoable };
  addUndoable(undoable);
};

const rotateParticles = ({ moleculeIndex, local }: PickedParticles, axis: Vector3, radians: number) => {
  const store = useStore.getState().projectState.testMolecules[moleculeIndex];
  const live = useRefStore.getState().moleculesRef?.current?.[moleculeIndex];
  if (!live) return;
  for (const i of local) {
    const a = live.atoms[i];
    if (!a.orientation) continue;
    a.orientation.applyAxisAngle(axis, radians).normalize();
    a.angularVelocity?.applyAxisAngle(axis, radians);
    a.initialOrientation?.copy(a.orientation);
    const own = store?.atoms[i];
    if (own) {
      own.orientation = a.orientation.clone();
      own.initialOrientation = a.orientation.clone();
    }
  }
  usePrimitiveStore.getState().updateViewer();
  usePrimitiveStore.getState().setChanged(true);
  markEngineDirty({ moved: true });
};

/** Turn the picked rods about a lab axis, each about its own center. */
export const rotateParticlesWithUndo = (axis: Vector3, degrees: number): void => {
  const picked = pickedParticles();
  if (!picked || blockedWhileSimulating()) return;
  const radians = (degrees * Math.PI) / 180;
  rotateParticles(picked, axis, radians);
  useStore.getState().addUndoable({
    name: picked.local.length > 1 ? 'Rotate Selected Particles' : 'Rotate Selected Particle',
    timestamp: Date.now(),
    blocked: blockedWhileSimulating,
    undo: () => rotateParticles(picked, axis, -radians),
    redo: () => rotateParticles(picked, axis, radians),
  } as Undoable);
};

/** Cut the picked particles onto the clipboard, as one undoable step. */
export const cutPickedParticles = (): boolean => removePicked(true);

/** Delete the picked particles, as one undoable step. */
export const deletePickedParticles = (): boolean => removePicked(false);
